import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import SidebarListItemLayout from '@/components/Sidebar/SidebarListItemLayout';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useAuthStore, useToastStore, useModalStore } from '@/store';
import PropTypes from 'prop-types';
import useResponsiveness from '@/hooks/useResponsiveness';
import useTranslation from '@/hooks/useTranslation';
import { AGENT_ICON } from '@/constants/config';
import { Image } from 'expo-image';
import ModelDropdown from '@/components/Dropdowns/ModelDropdown';

// Create styles outside component
const createStyles = (colors, isXS) => StyleSheet.create({
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  title: {
    minWidth: 0,
  },
  modelImage: {
    width: isXS ? 18 : 16,
    height: isXS ? 18 : 16,
    borderRadius: 0,
    backgroundColor: '#fff',
    marginRight: isXS ? 8 : 6,
  },
  placeholderImage: {
    backgroundColor: colors.solid_color_secondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

// Memoized model image component
const ModelImage = React.memo(({ imageUrl, colors, styles }) => {
  if (imageUrl) {
    return (
      <Image
        source={{ uri: imageUrl }}
        style={styles.modelImage}
        accessibilityLabel="Model logo"
        contentFit="cover"
      />
    );
  }

  return (
    <View style={[styles.modelImage, styles.placeholderImage]}>
      <Icon 
        name={AGENT_ICON}
        size={10} 
        color={colors.text_color_secondary} 
      />
    </View>
  );
});

ModelImage.propTypes = {
  imageUrl: PropTypes.string,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

ModelImage.displayName = 'ModelImage';

function SidebarListItemModel({
  title,
  isActive,
  href,
  imageUrl = 'https://gab.ai/images/placeholder.png',
  createdAt,
  shouldShowNewBadge = true,
  id,
  ...props
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { openModal } = useModalStore(state => state.actions);
  const toggleFavoriteModel = useAuthStore(state => state.toggleFavoriteModel);
  const favoriteModelIds = useAuthStore(state => state.user?.favoriteModelIds) || [];
  const isSaved = favoriteModelIds.includes(id) || false;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const { addToast } = useToastStore(state => state.actions);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const favoriteModelsCount = favoriteModelIds?.length || 0;

  const isNew = useMemo(() => {
    if (!createdAt) return false;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return new Date(createdAt) > sevenDaysAgo;
  }, [createdAt, shouldShowNewBadge]);

  // Memoize styles
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  // Memoize handlers
  const handleToggleFavorite = useCallback(async () => {
    try {
      if (!isSaved && isGhost && favoriteModelsCount >= 1) {
        openModal('GhostSignupModal');
        return;
      }
      if (!isSaved && !isPlus && favoriteModelsCount >= 5) {
        openModal('PlusFeatureModal', { feature: 'favorite-models' });
        return;
      }

      const success = await toggleFavoriteModel(id);
      if (success) {
        addToast(isSaved ? t('sidebar.modelRemovedFromFavorites') : t('sidebar.modelAddedToFavorites'), 'success');
      } else {
        addToast(isSaved ? t('sidebar.failedToUnsaveModel') : t('sidebar.failedToSaveModel'), 'error');
      }
    } catch (error) {
      if (error?.name === 'ApiError' && error?.message) {
        addToast(error.message, 'error', error?.data?.actions);
      } else {
        addToast(isSaved ? t('sidebar.failedToUnsaveModel') : t('sidebar.failedToSaveModel'), 'error');
      }
    }
  }, [toggleFavoriteModel, id, addToast, isSaved, favoriteModelsCount, isGhost, isPlus, openModal, t]);

  // Memoize title component
  const titleWithImage = useMemo(() => (
    <View style={styles.titleContainer}>
      <ModelImage 
        imageUrl={imageUrl} 
        colors={colors} 
        styles={styles} 
      />
      <Text 
        numberOfLines={1}
        size={isXS ? 'm' : 'n'}
        style={[
          styles.title,
          { color: isActive ? colors.accent_text_color : colors.text_color_primary }
        ]}
      >
        {title}
      </Text>
    </View>
  ), [imageUrl, colors, styles, isActive, title]);

  // Memoize content
  const content = useMemo(() => (
    <SidebarListItemLayout
      title={titleWithImage}
      isActive={isActive}
      onDropdownStateChange={setIsDropdownOpen}
      dropdownContent={
        <ModelDropdown
          modelId={id}
          isSaved={isSaved}
          onToggleSave={handleToggleFavorite}
          noRoot
        />
      }
      onPress={href && !isDropdownOpen ? () => router.push(href) : undefined}
      accessibilityLabel={`Model: ${title}`}
      accessibilityRole="button"
      accessibilityState={{
        selected: isActive,
        expanded: isDropdownOpen
      }}
      {...props}
    />
  ), [titleWithImage, isActive, isSaved, id,
     handleToggleFavorite, props, router, isDropdownOpen, href
  ]);

  return content;
}

SidebarListItemModel.propTypes = {
  title: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  href: PropTypes.string,
  imageUrl: PropTypes.string,
  id: PropTypes.string.isRequired,
};

SidebarListItemModel.displayName = 'SidebarListItemModel';

export default React.memo(SidebarListItemModel); 